<template>
  <v-container>
    <v-container>
      <v-card>
        <v-card-title>
          {{ $t("navOrders") }}
        </v-card-title>
        <v-card-text>
          <v-container class="py-5 px-3">
            <v-row>
              <v-col cols="12" sm="2" class="py-0">
                <v-menu
                  v-model="datepickerFrom"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="
                        moment(selectedFilters.date_from).format('DD.MM.YYYY')
                      "
                      :label="$t('paymentsFilterStart')"
                      type="text"
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-icon="mdi-calendar-start"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :max="moment.parseZone().format('YYYY-MM-DD')"
                    v-model="selectedFilters.date_from"
                    no-title
                    scrollable
                    :locale="$i18n.locale"
                    @change="handleDateChange"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-menu
                  v-model="datepickerTo"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="
                        moment(selectedFilters.date_to).format('DD.MM.YYYY')
                      "
                      :label="$t('paymentsFilterEnd')"
                      type="text"
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-icon="mdi-calendar-end"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :min="
                      moment
                        .parseZone(selectedFilters.date_from)
                        .format('YYYY-MM-DD')
                    "
                    :max="moment.parseZone().format('YYYY-MM-DD')"
                    v-model="selectedFilters.date_to"
                    no-title
                    scrollable
                    :locale="$i18n.locale"
                    @change="handleDateChange"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-select
                  :items="periodOptions"
                  v-model="selectedPeriod"
                  @change="updatePeriod"
                  dense
                  :label="$t('ordersPeriod')"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-select
                  :items="filtersStatuses"
                  item-text="title"
                  item-value="id"
                  v-model="selectedStatuses"
                  :label="$t('ordersStatus')"
                  multiple
                  dense
                  @change="handleStatusChange"
                >
                  <template slot="no-data">
                    <span class="grey--text caption ml-1">{{
                      $t("placeholderLoading")
                    }}</span>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0">{{ item.title }}</span>
                    <span v-if="index === 1" class="grey--text caption ml-1">
                      (+{{ selectedStatuses.length - 1 }})
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="selectedStatuses.includes(item.id)"
                      ></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
              <v-col class="py-0 col-auto ml-auto">
                <v-btn
                  @click="uploadOrders((csv = true))"
                  outlined
                  color="green darken-1"
                  text
                  tile
                  :loading="loadingCSV"
                  :disabled="orders == null || orders == [] || loadingCSV"
                  ><v-icon left>mdi-file-export</v-icon>CSV</v-btn
                >
              </v-col>
            </v-row>
          </v-container>
          <v-data-table
            v-if="orders"
            :headers="headers"
            :items="orders"
            :items-per-page="20"
            class="elevation-1"
            :no-data-text="$t('ordersNotFound')"
            :footer-props="{
              'items-per-page-options': limits,
              'items-per-page-text': $t('paymentsRowsPerPage'),
            }"
            :server-items-length="totalPages"
            :page.sync="currentPage"
            :items-per-page.sync="selectedFilters.limit"
            @update:page="onPageUpdate"
            @update:items-per-page="onItemsPerPageUpdate"
          >
            <template v-slot:item.id="{ item }">
              <router-link
                :to="{ name: 'order', params: { order_id: item.id } }"
                target="_blank"
                style="text-decoration: none"
                >{{ item.id }}</router-link
              >
            </template>
            <template v-slot:item.created="{ item }">
              <span>{{ item.created | dateAndTimeFilter }}</span>
            </template>
            <template v-slot:item.order_type="{ item }">
              <span>{{ findOrderTypeNameById(item.order_type) }}</span>
            </template>
            <template v-slot:item.status="{ item }">
              <span>{{ findOrderStatusNameById(item.status) }}</span>
            </template>
            <template v-slot:item.staff_user_id="{ item }">
              <span>{{ findStaffUserEmailById(item.staff_user_id) }}</span>
            </template>
          </v-data-table>
          <v-skeleton-loader
            v-else
            class="mx-n3 mt-n4"
            type="table-thead, table-tbody, table-tfoot"
            tile
          ></v-skeleton-loader>
        </v-card-text>
      </v-card>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment/moment";
import store from "@/store";

export default {
  name: "LocationOrders",

  beforeRouteEnter: async (to, from, next) => {
    await store.dispatch("loadLocation", to.params.location_id);
    next();
  },

  data() {
    return {
      orders: [],
      headers: [
        {
          text: this.$i18n.t("ordersID"),
          value: "id",
          width: "1%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersCreated"),
          value: "created",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersOrderType"),
          value: "order_type",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersStatus"),
          value: "status",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersCost"),
          value: "cost",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersPaid"),
          value: "paid",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersPurchase"),
          value: "purchase",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersDiscount"),
          value: "discount",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersPromocode"),
          value: "promocode",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersManager"),
          value: "staff_user_id",
          width: "5%",
          sortable: false,
        },
        {
          text: this.$i18n.t("ordersSource"),
          value: "source",
          width: "5%",
          sortable: false,
        },
      ],
      orderTypes: [
        {
          id: 1,
          title: this.$i18n.t("BAFormPaid"),
        },
        {
          id: 2,
          title: this.$i18n.t("BAFormTech"),
        },
        {
          id: 3,
          title: this.$i18n.t("BAFormFree"),
        },
        {
          id: 4,
          title: this.$i18n.t("BAFormEvent"),
        },
      ],
      selectedStatuses: [],
      filtersStatuses: [
        {
          id: 6,
          title: this.$i18n.t("locationOrderStatus.fullPaid"),
        },
        {
          id: 3,
          title: this.$i18n.t("locationOrderStatus.confirmed"),
        },
        {
          id: 2,
          title: this.$i18n.t("locationOrderStatus.notPaid"),
        },
        {
          id: 4,
          title: this.$i18n.t("locationOrderStatus.canceled"),
        },
      ],
      orderStatuses: [
        {
          id: 1,
          title: this.$i18n.t("locationOrderStatus.cart"),
        },
        {
          id: 2,
          title: this.$i18n.t("locationOrderStatus.notPaid"),
        },
        {
          id: 3,
          title: this.$i18n.t("locationOrderStatus.confirmed"),
        },
        {
          id: 4,
          title: this.$i18n.t("locationOrderStatus.canceled"),
        },
        {
          id: 5,
          title: this.$i18n.t("locationOrderStatus.cartCanceled"),
        },
        {
          id: 6,
          title: this.$i18n.t("locationOrderStatus.fullPaid"),
        },
      ],
      selectedPeriod: "",
      periodOptions: [
        { text: this.$i18n.t("ordersFilterPast"), value: "past" },
        { text: this.$i18n.t("ordersFilterYesterday"), value: "yesterday" },
        { text: this.$i18n.t("ordersFilterToday"), value: "today" },
      ],
      usersData: [],
      loadingCSV: false,
      ordersCSV: null,
      datepickerFrom: false,
      datepickerTo: false,
      selectedFilters: {
        date_from: moment.parseZone().startOf("month").format("YYYY-MM-DD"),
        date_to: moment.parseZone().format("YYYY-MM-DD"),
        offset: 0,
        limit: 10,
        status: "",
      },
      totalPages: null,
      limits: [10, 15, 30, 50, 100],
      currentPage: 1,
    };
  },
  mounted() {
    this.uploadOrders();
  },
  computed: {
    ...mapGetters(["location", "locationOrders", "staffUser"]),
  },
  filters: {
    dateAndTimeFilter(value) {
      if (value) {
        return moment.parseZone(value).format("DD.MM.YYYY HH:mm");
      }
    },
  },
  methods: {
    ...mapActions(["loadLocationOrders", "getOrdersCSV", "loadStaffUser"]),
    async sendLoadLocationOrdersRequest(payload) {
      this.orders = null;
      try {
        await this.loadLocationOrders(payload);
        if (this.locationOrders.results) {
          const response = this.locationOrders;
          this.orders = response.results;
          this.totalPages = response.count;
          await this.preloadStaffEmails();
        }
      } catch (error) {
        console.error(error);
      }
    },
    uploadOrders(csv) {
      this.selectedFilters.offset =
        this.selectedFilters.limit * (this.currentPage - 1);
      const filteredFilters = Object.entries(this.selectedFilters).reduce(
        (a, [k, v]) =>
          typeof v === "object" && !v.length ? a : ((a[k] = v), a),
        {}
      );

      const payload = Object.keys(filteredFilters)
        .map((key) => key + "=" + filteredFilters[key])
        .join("&");

      if (csv) {
        this.loadingCSV = true;
        this.getOrdersCSV(payload).then((response) => {
          if (response.data) {
            this.ordersCSV = response.data;
            this.loadingCSV = false;
            this.downloadCSV();
          } else {
            this.ordersCSV = null;
          }
        });
      } else {
        this.sendLoadLocationOrdersRequest(payload);
      }
    },
    downloadCSV() {
      const anchor = document.createElement("a");
      anchor.href =
        "data:text/csv;charset=utf-8," + encodeURIComponent(this.ordersCSV);
      anchor.target = "_blank";
      anchor.download = "orders.csv";
      anchor.click();
    },
    async preloadStaffEmails() {
      const ordersWithStaff = this.orders.filter(
        (order) => order.staff_user_id
      );
      this.usersData = await Promise.all(
        ordersWithStaff.map(async (order) => {
          return await this.loadStaffUserAndReturnData(order.staff_user_id);
        })
      );
    },
    async loadStaffUserAndReturnData(userId) {
      await this.loadStaffUser(userId);
      return {
        id: userId,
        email: this.staffUser.email,
      };
    },
    findOrderTypeNameById(typeId) {
      const type = this.orderTypes.find((t) => t.id === typeId);
      return type ? type.title : `ID: ${typeId}`;
    },
    findOrderStatusNameById(statusId) {
      const status = this.orderStatuses.find((s) => s.id === statusId);
      return status ? status.title : `ID: ${statusId}`;
    },
    findStaffUserEmailById(userId) {
      const user = this.usersData.find((u) => u.id === userId);
      return user ? user.email : "";
    },
    onPageUpdate(newPage) {
      this.currentPage = newPage;
      this.uploadOrders();
    },
    onItemsPerPageUpdate(newLimit) {
      this.selectedFilters.limit = newLimit;
      this.uploadOrders();
    },
    updatePeriod(period) {
      const today = moment();
      const yesterday = moment().subtract(1, "days");
      const lastYear = moment().subtract(1, "years");

      switch (period) {
        case "past":
          this.selectedFilters.date_from = lastYear.format("YYYY-MM-DD");
          this.selectedFilters.date_to = yesterday.format("YYYY-MM-DD");
          break;
        case "yesterday":
          this.selectedFilters.date_from = yesterday.format("YYYY-MM-DD");
          this.selectedFilters.date_to = yesterday.format("YYYY-MM-DD");
          break;
        case "today":
          this.selectedFilters.date_from = today.format("YYYY-MM-DD");
          this.selectedFilters.date_to = today.format("YYYY-MM-DD");
          break;
      }

      this.uploadOrders();
    },
    handleDateChange() {
      this.selectedPeriod = "";
      this.uploadOrders();
    },
    handleStatusChange() {
      this.selectedFilters.status = this.selectedStatuses.join(",");
      this.uploadOrders();
    },
  },
};
</script>
